import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import "moment/locale/gu";
import {
  ArrowLeft,
  Building2,
  Phone,
  User,
  MessageCircle,
  MapPin,
  Calendar,
  Tag,
  Pencil,
  CreditCard,
  IndianRupee,
} from "lucide-react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getLoggingUser } from "../../../features/loggingUser/loggingUserSlice";
import { getSelectedBranch } from "../../../features/settings/settingsSlice";
import {
  setEditParty,
  togglePartyModal,
} from "../../../features/parties/partiesSlice";
import { useCanAccess } from "../../../hooks/useCanAccess";
import { GET, PUT } from "../../../services/axios.service";
import { toastStyles } from "../../../static/styles";
import { t } from "../../../locales";
import { route } from "../../../static/route";
import { IFormInput } from "../../../types/parties";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import PartyFormModal from "../../../components/parties/PartyFormModal";
import { getTypeChips } from "../../../components/parties/typeBadge";

const PartyViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const loggingUser = useAppSelector(getLoggingUser);
  const selectedBranch = useAppSelector(getSelectedBranch);
  const [party, setParty] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  moment.locale("gu");

  const fetchParty = () => {
    if (!id) return;
    setLoading(true);
    GET(`/v1/parties/${id}`, {})
      .then((res: any) => {
        if (res.data.status === "success") setParty(res.data.data);
        else toast.error(res.data.message, { ...(toastStyles as any) });
      })
      .catch((err: any) => {
        toast.error(
          err?.response?.data?.message || t.common.somethingWentWrong,
          { ...(toastStyles as any) }
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (loggingUser?.permissions && selectedBranch && !canAccess("parties.menu")) {
      navigate(route.dashboard);
    }
  }, [loggingUser, selectedBranch]);

  useEffect(() => {
    fetchParty();
  }, [id]);

  const handleEditParty = () => {
    if (!party) return;
    dispatch(setEditParty(party));
    dispatch(togglePartyModal(true));
  };

  const handleSaveParty = async (data: IFormInput) => {
    if (!party?.id) return;
    try {
      const response: any = await PUT(`/v1/parties/${party.id}`, {
        firm_name: data.firm_name,
        name: data.name,
        process_definition_ids: data.process_definition_ids || [],
        phone: data.phone,
        wp_phone: data.wp_phone,
        address: data.address,
      });
      if (response.data.status === "success") {
        await dispatch(setEditParty(null));
        toast.success(response.data.message, { ...(toastStyles as any) });
        fetchParty();
      } else {
        toast.error(response.data.message, { ...(toastStyles as any) });
      }
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.message ||
          t.common.somethingWentWrong,
        { ...(toastStyles as any) }
      );
    }
  };

  const typeChips = getTypeChips(party?.type);

  return (
    <Layout>
      <div className="px-2 py-4 md:px-6 md:py-6">
        {/* Breadcrumb */}
        <nav className="text-sm font-semibold text-gray-500 mb-4">
          <Link to={route.dashboard} className="text-brand-dark hover:underline">
            {t.parties.breadcrumbHome}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          <Link to={route.parties} className="text-brand-dark hover:underline">
            {t.parties.breadcrumbParties}
          </Link>
          <span className="mx-2">&rsaquo;</span>
          <span className="text-gray-500">વિગતો</span>
        </nav>

        {/* Page Title */}
        <h1 className="text-2xl font-bold text-[#113F67] mb-6">પાર્ટી ની વિગતો</h1>

        {/* Content */}
        {loading ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.parties.loading}
              </div>
            </CardContent>
          </Card>
        ) : !party ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              {t.parties.noRecords}
            </CardContent>
          </Card>
        ) : (
          <Card className="shadow-lg border-0">
            <CardContent className="p-0">
              {/* Header */}
              <div className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#113F67]/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="h-5 w-5 text-[#113F67]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-[#113F67]">પાર્ટી</h2>
                      <p className="text-sm text-gray-500">ID: {party.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      className="h-10 px-4 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67] text-sm"
                      onClick={() => navigate(route.parties)}
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      પાછા જાઓ
                    </Button>
                    <Button
                      className="h-10 px-4 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67] text-sm"
                      onClick={() => navigate(route.payments.replace(":id", String(party.id)))}
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      એકાઉન્ટ
                    </Button>
                    {canAccess("parties.rate") && (
                      <Button
                        variant="outline"
                        className="h-10 px-4 rounded-lg border-[#113F67] text-[#113F67] hover:bg-[#113F67] hover:text-white text-sm"
                        onClick={() => navigate(route.partiesRates.replace(":id", String(party.id)))}
                      >
                        <IndianRupee className="h-4 w-4 mr-2" />
                        {t.processRates.pageTitle}
                      </Button>
                    )}
                    {canAccess("parties.update") && (
                      <Button
                        variant="outline"
                        className="h-10 px-4 rounded-lg border-[#113F67] text-[#113F67] hover:bg-[#113F67] hover:text-white text-sm"
                        onClick={handleEditParty}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        એડિટ કરો
                      </Button>
                    )}
                  </div>
                </div>

                {/* Meta row */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  {typeChips.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Tag className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-500">{t.parties.typeLabel}:</span>
                      {typeChips.map((chip, i) => (
                        <Badge key={i} className={`text-xs ${chip.className}`}>
                          {chip.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">{t.parties.ratesCountLabel}: </span>
                    <span className="font-medium text-gray-700">{party.process_rates_count ?? 0}</span>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200" />

              {/* Party Information */}
              <div className="p-4 md:p-6">
                <h3 className="text-base font-bold text-[#113F67] mb-3">
                  પાર્ટી ની માહિતી
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.parties.firmNameLabel}</p>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800">
                        {party.firm_name || "-"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.parties.nameLabel}</p>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800">
                        {party.name || "-"}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.parties.phoneLabel}</p>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      {party.phone ? (
                        <a
                          href={`tel:${party.phone}`}
                          className="text-sm font-medium text-[#113F67] hover:underline"
                        >
                          {party.phone}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t.parties.wpPhoneLabel}</p>
                    <div className="flex items-center gap-2">
                      <MessageCircle className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      {party.wp_phone ? (
                        <a
                          href={`https://wa.me/${party.wp_phone.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-[#113F67] hover:underline"
                        >
                          {party.wp_phone}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <p className="text-xs text-gray-500 mb-1">{t.parties.addressLabel}</p>
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-800 whitespace-pre-line">
                        {party.address || "-"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200" />

              {/* Account Info */}
              <div className="p-4 md:p-6">
                <h3 className="text-base font-bold text-[#113F67] mb-4">
                  એકાઉન્ટ માહિતી
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">બનાવ્યાની તારીખ</p>
                    <div className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {party.created_by_user?.name || "-"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {moment(party.created_at).format("DD MMMM, YYYY hh:mm A")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">છેલ્લે અપડેટ કર્યું</p>
                    <div className="flex items-start gap-2">
                      <Calendar className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">
                          {party.updated_by_user?.name || "-"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {moment(party.updated_at).format("DD MMMM, YYYY hh:mm A")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <PartyFormModal handleSaveParty={handleSaveParty} />
      </div>
    </Layout>
  );
};

export default PartyViewPage;
